import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { ValueEditor } from "@solomon-apps/shared/src/components/ui/value-editor";
import React from "react";

import { formatMagnitude } from "@/pages/ProgramPolicies/components/PolicyValueEditor";

import type { PolicyDefinition } from "./policiesMocks";

/** Units that route to specialized controls instead of the generic value editor. */
const SPECIALIZED_UNITS = ["multiplier", "tier"];

/**
 * Whether a policy's value is rendered by `<PolicyValueField>` (the
 * ValueEditor-backed affordance) versus one of the specialized controls.
 * True for: bounded number (constrained), unbounded number
 * (delegated), bounded days, and bounded percent.
 */
export function policyUsesValueField(policy: PolicyDefinition): boolean {
    const { constraint, permission } = policy;
    const isBoundedNumber =
        constraint.kind === "range" &&
        permission === "constrained" &&
        !SPECIALIZED_UNITS.includes(constraint.unit);
    const isUnboundedNumber =
        constraint.kind === "range" &&
        permission === "delegated" &&
        !SPECIALIZED_UNITS.includes(constraint.unit);
    const isBoundedDays =
        constraint.kind === "range" && constraint.unit === "days";
    const isBoundedPercent =
        constraint.kind === "range" && constraint.unit === "%";
    return (
        isBoundedNumber ||
        isUnboundedNumber ||
        isBoundedDays ||
        isBoundedPercent
    );
}

interface PolicyValueFieldProps {
    /** The policy whose constraint + permission drive the editor props. */
    policy: PolicyDefinition;
    /** Controlled numeric value as a string (the draft model). */
    value: string;
    onChange: (next: string) => void;
    /** Error state — paints the amount + rail red. */
    invalid: boolean;
}

/**
 * Policy-aware wrapper around the generic `<ValueEditor>`. Translates a
 * policy's `constraint` + `permission` into the editor's
 * variant/unit/formatter/notch props — the one named place where
 * policy semantics meet the pure-mechanism editor. The editor itself
 * knows nothing about policies; this is the mechanism/policy split.
 */
export function PolicyValueField({
    policy,
    value,
    onChange,
    invalid
}: PolicyValueFieldProps): React.ReactElement {
    const { constraint, permission } = policy;
    const isDays = constraint.kind === "range" && constraint.unit === "days";
    const isPercent = constraint.kind === "range" && constraint.unit === "%";
    // Delegated plain number → unbounded magnitude editor.
    const isUnboundedNumber =
        constraint.kind === "range" &&
        permission === "delegated" &&
        !SPECIALIZED_UNITS.includes(constraint.unit);
    const rangeMin = constraint.kind === "range" ? constraint.min : 0;
    const rangeMax = constraint.kind === "range" ? constraint.max : 0;
    // No issuer ceiling (floor-only) renders the unbounded variant (no bar).
    const noMax =
        constraint.kind === "range" && !Number.isFinite(constraint.max);

    // % notches snap to round quarters (0/25/50/75/100) that fall in range,
    // with min/max as endpoints — positioned by their true value.
    const percentNotchValues = isPercent
        ? Array.from(
              new Set([
                  rangeMin,
                  ...[0, 25, 50, 75, 100].filter(
                      (v) => v > rangeMin && v < rangeMax
                  ),
                  rangeMax
              ])
          ).sort((a, b) => a - b)
        : undefined;

    return (
        <ValueEditor
            className={isDays || isPercent ? "max-w-xs" : "max-w-xl"}
            variant={isUnboundedNumber || noMax ? "unbounded" : "bounded"}
            value={value}
            onChange={onChange}
            invalid={invalid}
            min={rangeMin}
            max={rangeMax}
            divisions={isDays ? 3 : 5}
            notchValues={percentNotchValues}
            unit={
                isDays || isPercent ? (
                    <Text
                        variant="body"
                        render={<span />}
                        className="shrink-0 text-foreground-muted"
                    >
                        {isDays ? "days" : "%"}
                    </Text>
                ) : null
            }
            formatValue={
                isPercent
                    ? (n): string => `${n}%`
                    : isDays
                      ? (n): string => `${n}d`
                      : formatMagnitude
            }
        />
    );
}
